import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import WarningIcon from '@material-ui/icons/Warning';
import DirectionsIcon from '@material-ui/icons/Directions';
import BlockIcon from '@material-ui/icons/Block';
import RoomIcon from '@material-ui/icons/Room';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 60,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  routeIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  warningBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  stopsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stopItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  stopIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  stopConnector: {
    width: 2,
    height: 30,
    backgroundColor: theme.palette.divider,
    margin: '0 auto',
  },
}));

const RouteDeactivatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchRoute();
  }, [id]);

  const fetchRoute = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const routeDoc = doc(firestore, 'routes', id);
      const routeSnapshot = await getDoc(routeDoc);
      
      if (routeSnapshot.exists()) {
        setRoute({
          id: routeSnapshot.id,
          ...routeSnapshot.data()
        });
      } else {
        // Si l'itinéraire n'existe pas, utiliser des données de démonstration
        setRoute({
          id: id,
          name: 'Paris - Lyon',
          origin: 'Paris',
          destination: 'Lyon',
          distance: 465,
          duration: 270, // en minutes
          status: 'Active',
          type: 'Direct',
          price: 45.99,
          stops: [],
          description: 'Itinéraire direct entre Paris et Lyon, passant par l\'autoroute A6.'
        });
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'itinéraire',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmDeactivate = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const routeDoc = doc(firestore, 'routes', id);
      await updateDoc(routeDoc, {
        status: 'Inactive',
        deactivatedAt: new Date(),
        deactivatedReason: 'Désactivé par l\'administrateur'
      });
      
      setSnackbar({
        open: true,
        message: 'Itinéraire désactivé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/routes');
      }, 1500);
      
    } catch (error) {
      console.error('Error deactivating route:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la désactivation de l\'itinéraire',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancelDeactivate = () => {
    setOpenConfirmDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/routes');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Désactiver un itinéraire
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : route ? (
        <Paper className={classes.paper}>
          <Box className={classes.warningBox}>
            <DirectionsIcon className={classes.routeIcon} />
            <BlockIcon className={classes.warningIcon} />
            <Typography variant="h5" gutterBottom>
              Vous êtes sur le point de désactiver l'itinéraire suivant :
            </Typography>
            <Typography variant="h6" gutterBottom>
              {route.name}
            </Typography>
            
            <div className={classes.stopsContainer}>
              <div className={classes.stopItem}>
                <RoomIcon className={classes.stopIcon} style={{ color: '#4caf50' }} />
                <Typography variant="subtitle1">
                  <strong>{route.origin}</strong> (Départ)
                </Typography>
              </div>
              
              {route.stops && route.stops.length > 0 && (
                <>
                  {route.stops.map((stop, index) => (
                    <React.Fragment key={index}>
                      <div className={classes.stopConnector} />
                      <div className={classes.stopItem}>
                        <RoomIcon className={classes.stopIcon} style={{ color: '#ff9800' }} />
                        <Typography variant="subtitle1">
                          {stop} (Arrêt)
                        </Typography>
                      </div>
                    </React.Fragment>
                  ))}
                </>
              )}
              
              <div className={classes.stopConnector} />
              <div className={classes.stopItem}>
                <RoomIcon className={classes.stopIcon} style={{ color: '#f44336' }} />
                <Typography variant="subtitle1">
                  <strong>{route.destination}</strong> (Arrivée)
                </Typography>
              </div>
            </div>
            
            <Grid container spacing={2} justifyContent="center" style={{ marginTop: 16, maxWidth: 600 }}>
              <Grid item xs={4} style={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">Distance</Typography>
                <Typography variant="body1">{route.distance} km</Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">Durée</Typography>
                <Typography variant="body1">{formatDuration(route.duration)}</Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">Prix</Typography>
                <Typography variant="body1">{formatPrice(route.price)}</Typography>
              </Grid>
            </Grid>
            
            <Typography variant="body1" paragraph style={{ marginTop: 24 }}>
              <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8, color: '#f44336' }} />
              La désactivation d'un itinéraire aura les conséquences suivantes :
            </Typography>
            
            <Box textAlign="left" width="100%" maxWidth="600px" margin="0 auto">
              <Typography variant="body1" paragraph>
                • L'itinéraire ne sera plus disponible pour la création de nouveaux voyages
              </Typography>
              <Typography variant="body1" paragraph>
                • Les voyages déjà programmés sur cet itinéraire resteront actifs
              </Typography>
              <Typography variant="body1" paragraph>
                • L'itinéraire restera visible dans la liste mais sera marqué comme inactif
              </Typography>
              <Typography variant="body1" paragraph>
                • Les statistiques de l'itinéraire seront conservées
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              Cette action peut être annulée ultérieurement en modifiant le statut de l'itinéraire.
            </Typography>
            
            <Grid container spacing={2} justifyContent="center" style={{ marginTop: 16 }}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                >
                  Annuler
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDeactivate}
                >
                  Désactiver l'itinéraire
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Itinéraire non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDeactivate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la désactivation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir désactiver l'itinéraire {route?.name} ? 
            Cette action affectera la disponibilité de cet itinéraire pour les futurs voyages.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeactivate} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDeactivate} color="secondary" autoFocus>
            Confirmer la désactivation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RouteDeactivatePage;
